<template>
  <div class="container-login">
    <div class="container-login-layout login-left">
      <div class="container-login-contents">
        <LoginCarousel />
      </div>
    </div>

    <div class="container-login-layout login-right">
      <div class="container-login-detail">
        <div class="container-login-contents fade-in">
          <div class="login-head">
            <div class="h2-wrap">
              <h2 class="medium text-gradient">{{ t('ai_realtime_interpretation') }}</h2>
              <h2 class="medium">{{ t('start_now') }}</h2>
            </div>
            <Head class="move-up mt-3" />
            <!-- <button type="button" class="button-text language-wrapper" @click="toggleLangModal">
            <span v-html="t('login_language_to_interpret')" />
          </button> -->
          </div>
          <div class="login-tab">
            <button type="button" class="tab" :class="{ on: !isSub }" @click="setLoginTypeHost">
              {{ t('ss_login_as_host') }}
            </button>
            <button type="button" class="tab" :class="{ on: isSub }" @click="setLoginTypeSub">
              {{ t('ss_login_as_sub') }}
            </button>
          </div>
          <div class="login-forms-area">
            <!--소셜미디어로그인-->
            <div v-show="!isSub" class="wrapper-login-forms mb-2">
              <button type="button" class="line-btn l-btn button-full" @click="onClickLoginGoogleCustom">
                <img :src="getAssetImageUrl('common/icons/google-logo.svg')" alt="logo" class="icon-small" />
                {{ t('continue_with_social', { social: 'Google' }) }}
              </button>

              <button
                id="profile-facebook"
                ref="refFacebookButton"
                type="button"
                class="line-btn l-btn button-full"
                @click="onClickLoginFacebook"
              >
                <img :src="getAssetImageUrl('common/icons/facebook-color.svg')" alt="logo" class="icon-small" />
                {{ t('continue_with_social', { social: 'Facebook' }) }}
              </button>
            </div>

            <!--서브계정로그인-->
            <div v-if="isSub" class="wrapper-login-forms mb-2">
              <FormLogin v-if="isSub" v-model:login-form="inputFormLogin" domain="SELF_SERVICE" :error-login />
              <ButtonProcess full-width :disabled="isProcessingLogin" @click="handleLoginSub">
                {{ t('login') }}
              </ButtonProcess>
            </div>

            <!--하위Action버튼들-->
            <div class="wrapper-login-forms">
              <div class="or-area">
                <span>{{ t('or') }}</span>
                <div class="line"></div>
              </div>
              <button type="button" class="gray-btn l-btn" @click="moveToEnterprisePage">
                {{ t('start_enterprise') }}
              </button>
              <a class="gray-link center mt-4" :href="supportUrl">
                {{ t('ss_login_have_issues') }}
              </a>
            </div>
          </div>
        </div>
      </div>

      <!--약관-->
      <div class="wrapper-policy fade-in">
        <a
          class="link-policy link-smaller link-dimmer"
          rel="noopener noreferrer"
          @click="openNewTab('ss-policy', { id: 'service' })"
        >
          {{ t('policy_service') }}
        </a>
        <a
          class="link-policy link-smaller link-dimmer"
          rel="noopener noreferrer"
          @click="openNewTab('ss-policy', { id: 'privacy' })"
        >
          {{ t('policy_privacy') }}
        </a>
        <a class="link-policy link-smaller link-dimmer" @click="openNewTab('ss-policy', { id: 'info' })">
          {{ t('policy_info') }}
        </a>
      </div>
    </div>

    <teleport to="body">
      <ModalSelectLanguage
        v-if="showLangModal"
        :current-language="getLanguageByCode(userStore.pageLanguage)"
        :subtitle-key="t('label_choose_display_language')"
        @close="toggleLangModal"
      />
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { loginSelfService, loginSelfServiceGoogle, requestAuthKey, requestLoginApp } from '@/api/apiService';
import FormLogin from '@/components/domains/chat/common/FormLogin.vue';
import Head from '@/components/domains/chat/common/Head.vue';
import ModalSelectLanguage from '@/components/domains/chat/common/ModalSelectLanguage.vue';
import LoginCarousel from '@/components/domains/login/LoginCarousel.vue';
import ButtonProcess from '@/components/shared/base/ButtonProcess.vue';
import { getLanguageByCode } from '@/static/languages';
import { useAdminStore } from '@/stores/admin';
import { useModalStore } from '@/stores/modal';
import { useToastStore } from '@/stores/toast';
import { useUserStore } from '@/stores/user';
import { TypeLoginChannel, Nullable } from '@/types/common';
import { IErrorLogin, ILoginForm } from '@/types/components';
import { IAppError } from '@/types/response';
import { GOOGLE_OAUTH_STATE } from '@/utils/constants/mobile';
import { isEmbeddedBrowser, isAndroid } from '@/utils/device';
import { encryptPassword } from '@/utils/encryptor';
import { getAssetImageUrl } from '@/utils/getAssetImageUrl';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const userStore = useUserStore();
const adminStore = useAdminStore();
const toastStore = useToastStore();
const modalStore = useModalStore();

const refFacebookButton = ref<HTMLButtonElement | null>(null); // 페이스북 로그인 버튼
const showLangModal = ref<boolean>(false);
const isSub = ref<boolean>(!!route.query.partnerCode); // 사용자가 SUB 인지 여부
const inputFormLogin = ref<ILoginForm>({
  partnerCode: '',
  userId: '',
  password: '',
  loginType: 'CUSTOMER',
});
const errorLogin = ref<IErrorLogin>();
const isProcessingLogin = ref<boolean>(false);
const popupGoogle = shallowRef<Nullable<Window>>(null);
let intervalCheckPopupClosed: Nullable<ReturnType<typeof setInterval>> = null;

// window.FB / window.google 를 쓸 수 있도록 해주는 타입 보완
declare global {
  interface Window {
    google?: any;
    fbAsyncInit?: () => void;
    FB?: any;
  }
}

const supportUrl = computed<string>(() => {
  const base = 'https://www.sconai.com/sconchat/home';
  const lang = userStore.pageLanguage;

  return lang ? `${base}?lang=${lang}#support` : `${base}#support`;
});

// 페이스북 SDK 초기화
const initFacebook = () => {
  // 1) Define fbAsyncInit BEFORE loading sdk.js
  window.fbAsyncInit = function () {
    window.FB.init({
      appId: '1742116097180481',
      xfbml: true,
      version: 'v23.0',
    });

    window.FB.getLoginStatus(function (response) {
      console.debug('is user logged in via FB?', response);
    });
  };

  // 2) Load Facebook SDK script ONLY ONCE
  if (!document.getElementById('facebook-jssdk')) {
    const js = document.createElement('script');
    js.id = 'facebook-jssdk';
    js.src = 'https://connect.facebook.net/en_US/sdk.js';
    js.async = true;
    js.defer = true;
    document.body.appendChild(js);
  }
};

// 커스텀 구글 버튼 클릭 시
const onClickLoginGoogleCustom = () => {
  if (isEmbeddedBrowser()) {
    modalStore.openModal({ modalKey: 'LoginGuideOpenBrowser' });
    return;
  }

  if (popupGoogle.value && !popupGoogle.value.closed) {
    popupGoogle.value.focus();
    return;
  }

  const urlOrigin = window.location.origin;

  const state = crypto.randomUUID();
  sessionStorage.setItem(GOOGLE_OAUTH_STATE, state);

  const params = new URLSearchParams({
    client_id: '60715395606-cmqq27tih5875l6q4ce5j8ajgpmd4lq7.apps.googleusercontent.com',
    redirect_uri: `${urlOrigin}/callback.html`,
    response_type: 'code',
    scope: 'openid email profile',
    prompt: 'select_account',
    state,
  });

  const urlAuth = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;

  if (isAndroid) {
    window.location.replace(urlAuth);
    return;
  }

  popupGoogle.value = window.open(urlAuth, 'google-login', 'width=500,height=600,left=400,top=200');

  if (!popupGoogle.value) {
    toastStore.error(t('error_popup_blocked'));
    return;
  }

  window.addEventListener('message', handleMessagePopup);

  intervalCheckPopupClosed = setInterval(() => {
    if (popupGoogle.value?.closed) {
      cleanupGooglePopup();
    }
  }, 500);
};

const handleMessagePopup = async (event: MessageEvent) => {
  if (event.origin !== window.location.origin) return;

  if (event.data?.error) {
    toastStore.error(t('google_signin_failed'));
    cleanupGooglePopup();
    return;
  }
  if (!event.data?.code) return;

  const stateSaved = sessionStorage.getItem(GOOGLE_OAUTH_STATE);
  if (!event.data?.state || event.data.state !== stateSaved) {
    toastStore.error(t('google_signin_failed_state'));
    cleanupGooglePopup();
    return;
  }

  sessionStorage.removeItem(GOOGLE_OAUTH_STATE);

  cleanupGooglePopup();
  await handleLoginGoogle(event.data.code);
};

const cleanupGooglePopup = () => {
  if (intervalCheckPopupClosed) {
    clearInterval(intervalCheckPopupClosed);
    intervalCheckPopupClosed = null;
  }

  popupGoogle.value?.close(); // 팝업 창 명시적 종료
  popupGoogle.value = null;

  window.removeEventListener('message', handleMessagePopup);
};

// 언어선택 모달 토글
const toggleLangModal = () => {
  showLangModal.value = !showLangModal.value;
};

const openNewTab = (name: string, query: Record<string, string>) => {
  const newUrl = router.resolve({ name, query }).href;
  window.open(newUrl, '_blank');
};

// 구글 로그인 시도 처리
const handleLoginGoogle = async (code) => {
  try {
    const resLogin = await loginSelfServiceGoogle('THIRD_PARTY_GOOGLE', code);
    if (!resLogin.success) {
      switch (resLogin.errorCode) {
        case 1004:
          goToSignup('google', '', resLogin.errorMessage);
          break;
        default:
          console.error('FAIL');
      }
      return;
    } else {
      handleLoginSuccess(resLogin.data);
    }
  } catch (err) {
    console.error('구글 로그인이 실패했습니다.', err);
  }
};

// 구글 로그인 버튼 클릭 시
const onClickLoginFacebook = () => {
  if (!window.FB) {
    console.warn('Facebook SDK not loaded yet');
    return;
  }

  window.FB.login(
    (loginRes: any) => {
      if (!loginRes.authResponse) {
        console.log('User cancelled login or did not fully authorize.');
        return;
      }

      (async () => {
        const { accessToken } = loginRes.authResponse;

        window.FB.api('/me', { fields: 'id,name,email' }, async function (meRes: any) {
          try {
            const resLogin = await loginSelfService('THIRD_PARTY_META', meRes.id, accessToken);

            if (!resLogin.success) {
              console.error('Login failed', resLogin);
              switch (resLogin.errorCode) {
                case 1004:
                  goToSignup('meta', meRes.id, accessToken);
                  break;
                default:
                  console.error('FAIL');
              }

              return;
            } else handleLoginSuccess(resLogin.data);
          } catch (err) {
            console.error('Network error', err);
          }
        });
      })().catch((err) => console.error('key/encryption error', err));
    },
    { scope: 'email' }
  );
};

// 로그인 성공 시
const handleLoginSuccess = async (authToken) => {
  localStorage.setItem('authToken', authToken as string);
  sessionStorage.setItem('authToken', authToken as string);
  localStorage.setItem('loginType', 'SELF_SERVICE');

  // 임시로 넣은 값입니다. 추후 API로 받은 값을 바인딩할 수 있습니다.
  localStorage.setItem('userType', 'HOST');
  localStorage.setItem('initializedPassword', false);

  await adminStore.fetchPartnerHome();
  await userStore.fetchUserInfo();
  movePage();

  localStorage.setItem('partner-code-admin', adminStore.getPartnerInfo.partnerCode);
};

const validateFormData = () => {
  const { partnerCode, userId, password } = inputFormLogin.value;

  if (!partnerCode.trim()) return 1015;
  if (!userId || !password) return 1002;

  return null;
};

const getLoginChannel = (): TypeLoginChannel => {
  const { redirectTo = '' } = route.query;

  if (redirectTo && redirectTo.includes('chat')) return 'CHAT';
  return 'SELF_SERVICE';
};

const moveToEnterprisePage = () => {
  window.location.href = `${window.location.origin}/admin/login/customer`;
};

const movePage = () => {
  const { redirectTo = '' } = route.query;
  let path = '';

  if (redirectTo) {
    path = redirectTo.toString();
  } else {
    path = '/ss/dashboard';
  }

  router.replace(path);
};

// 서브 로그인 처리
const handleLoginSub = async () => {
  const validationError = validateFormData();
  if (validationError) {
    handleErrorLogin({ errorCode: validationError } as IAppError);
    return;
  }

  isProcessingLogin.value = true;

  const { userId, partnerCode } = inputFormLogin.value;
  const loginChannel = getLoginChannel();

  try {
    const passwordEncrypted = await getEncryptedPassword();
    const { data, success } = await requestLoginApp(
      {
        userId,
        password: passwordEncrypted,
      },
      {
        type: 'SELF_SERVICE',
        partnerCode,
        loginChannel,
      }
    );

    if (success) {
      userStore.setLoginInfo({
        ...data,
        loginType: 'SELF_SERVICE',
        partnerCode,
      });

      await userStore.fetchUserInfo();
      movePage();
    }
  } catch (error) {
    handleErrorLogin(error as IAppError);
  } finally {
    isProcessingLogin.value = false;
  }
};

// 에러메시지
const handleErrorLogin = (responseError: IAppError) => {
  let messageDisplayed = '';

  switch (responseError.errorCode) {
    case 1001:
      messageDisplayed = 'invalid_access';
      break;
    case 1002:
    case 1004:
      messageDisplayed = 'failed_to_login_by_id_or_pw';
      break;
    case 1008:
      messageDisplayed = 'invalid_access';
      break;
    case 1015:
      messageDisplayed = 'login_no_such_partner_code';
      break;
    default:
      messageDisplayed = 'failed_to_login_for_unknown_reason';
      break;
  }
  errorLogin.value = { ...responseError, messageDisplayed };
};

// 회원가입 페이지로 이동
const goToSignup = (oAuth: string, userId: string, accessToken: string) => {
  localStorage.setItem('authToken', accessToken);
  sessionStorage.setItem('authToken', accessToken);
  router.push({
    name: 'ss-onboarding',
    query: {
      oAuth,
      userId,
      accessToken,
      referralCode: route.query.referralCode,
    },
  });
};

// 추가 사용자로 로그인
const setLoginTypeSub = () => (isSub.value = true);

// SNS로 로그인
const setLoginTypeHost = () => (isSub.value = false);

// 비밀번호 암호화
const getEncryptedPassword = async (): Promise<string> => {
  localStorage.removeItem('SC-USER-ID');
  localStorage.removeItem('authToken');
  sessionStorage.removeItem('authToken');

  const { data: { partnerPublicKey = '' } = { partnerPublicKey: '' } } = await requestAuthKey();
  return await encryptPassword(partnerPublicKey, inputFormLogin.value.password);
};

onMounted(async () => {
  const googleCode = route.query.googleCode;
  const googleError = route.query.googleError;

  if (googleError && typeof googleError === 'string') {
    toastStore.error(t('google_signin_failed'));

    const newQuery = { ...route.query };
    delete newQuery.googleError;

    router.replace({ query: newQuery });
    return;
  }

  const stateParam = route.query.state;
  const stateReturned = Array.isArray(stateParam) ? stateParam[0] : stateParam;
  const stateSaved = sessionStorage.getItem(GOOGLE_OAUTH_STATE);

  if (googleCode && typeof googleCode === 'string') {
    const queryNew = { ...route.query };
    delete queryNew.googleCode;
    delete queryNew.state;

    try {
      if (!stateReturned || stateReturned !== stateSaved) {
        toastStore.error(t('google_signin_failed_state'));
        return;
      }
      sessionStorage.removeItem(GOOGLE_OAUTH_STATE);

      await handleLoginGoogle(googleCode);
    } finally {
      router.replace({ query: queryNew });
    }

    return;
  }

  if (route.name === 'login-ss-sub') {
    setLoginTypeSub();
  }

  initFacebook();
});

onUnmounted(() => {
  cleanupGooglePopup();
});
</script>

<style scoped lang="scss">
.container-login {
  display: flex;
  width: 100%;
  min-height: 100dvh;
  height: 100dvh;

  @include respond-to(mobile-landscape) {
    flex-direction: column;
  }
}

.container-login-layout {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
  @include respond-to(mobile-landscape) {
    min-height: calc(100dvh - 1rem);
  }
}

.login-left {
  background: var(--surface-background);

  @include respond-to(mobile-landscape) {
    display: none;
  }
}

.login-right {
  background-color: var(--surface-default);
  height: 100%;
  justify-content: flex-start;
  .container-login-detail {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    max-width: 30rem;
    padding: 0 $spacing4;
    width: 100%;
    .container-login-contents {
      width: 100%;
      max-width: 100%;
    }
  }
  @include respond-to(mobile-landscape) {
    height: auto;
    overflow-y: auto;
    justify-content: space-between;
    .container-login-detail {
      padding: 2rem 1rem;
      flex: 1;
    }
    .wrapper-policy {
      flex: 0 0 auto;
    }
  }
}

.container-login-contents {
  display: flex;
  flex-direction: column;
  width: fit-content;
  max-width: 20rem;
  align-items: center;
  text-align: center;
  .login-head {
    .h2-wrap {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-wrap: wrap;
      column-gap: $spacing2;
      h2 {
        font-weight: 600;
      }
    }
    .login-head-container {
      justify-content: center;
    }
  }
  .login-tab {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin: $spacing7 0;
    .tab {
      min-height: 3.5rem;
      flex: 1;
      font-size: $font-size-p;
      color: $color-gray-90;
      border-radius: 100px;
      padding: $spacing3 $spacing4;
      word-break: keep-all;
      &.on {
        background: $color-navy-5;
        color: $color-navy-100;
      }
      &:hover {
        color: $color-navy-100;
      }
    }
  }
  .or-area {
    position: relative;
    padding: $spacing2 0;
    .line {
      position: absolute;
      width: 100%;
      height: 1px;
      background: $color-gray-40;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
    span {
      position: relative;
      z-index: 1;
      background: #fff;
      padding: 0 $spacing3;
      color: $color-gray-200;
    }
  }
}
.login-forms-area {
  width: 100%;
  min-height: 376px;
}

.wrapper-login-forms {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 0.5rem;

  .xl-btn {
    font-weight: 500;
  }
  .gray-link {
    color: $color-gray-200;
    font-weight: 500;
  }
  .primary-btn {
    margin-top: $spacing2;
  }
}

.wrapper-policy {
  display: flex;
  gap: 1rem;
  margin-top: auto;
  padding-bottom: 1rem;
}

.image {
  border-radius: 16px;
  background-color: var(--surface-default);
  height: 23rem;
  width: 100%;
}

:deep(.text-gradient) {
  display: inline-block;
  background: var(--font-gradient);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: gradientShift 10s ease infinite;
  font-weight: inherit;
}

@include respond-to(mobile-landscape) {
  .container-login-contents {
    .login-tab {
      flex-wrap: wrap;
      margin: 1.5rem 0 1rem;
      .tab {
        flex: auto;
      }
    }
  }
}
@media (max-height: 800px) {
  .login-right {
    .container-login-detail {
      justify-content: flex-start;
      padding: $spacing8 $spacing4 $spacing4;
      .h2-wrap {
        h2 {
          font-size: 1.5rem;
        }
      }
      .login-head-container {
        margin-bottom: $spacing2;
      }
      .login-tab {
        margin: 1rem 0 1.5rem;
      }
      .login-forms-area {
        min-height: initial;
      }
      .login-head {
        h2 {
          font-size: $font-size-h4;
        }
      }
      .sconchat-logo {
        height: 2rem;
      }
    }
  }
}
</style>
